/**
 * Kickard PHASE 3.13 — Luck Report screen.
 *
 * Route: /match-report/:matchId
 *
 * Central metric: "Average Match Card" (averageMatchCardValue).
 * Calculated from cards dealt in both halves + all cards drawn during the
 * match (Jokers excluded). Report data comes from the backend endpoint
 * `GET /api/match-reports/{matchId}` which returns full card lists only
 * to match participants (guestId gated).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadGuest } from '@/lib/guestIdentity';
import { loadPracticeTrace } from '@/lib/practiceLuckTrace';
import { PremiumBackButton, PremiumHomeButton } from '@/components/PremiumNav';
import { setScreen } from '@/lib/sessionTracking';
import { computeDeckAccountingCopy } from './deckAccountingCopy';
import { getRankedResult, formatSRDelta, formatTierChange } from '@/api/rankedClient';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MODE_LABEL = {
  quick_casual: 'Quick Match',
  friend: 'Friend Match',
  bot_practice: 'Practice',
};

const ENDED_BY_LABEL = {
  penalty: 'Penalties',
  forfeit: 'Forfeit',
  timeout: 'Timeout',
  disconnect: 'Disconnect',
  normal: 'Full Time',
  fulltime: 'Full Time',
};

const IMPORTANCE_LABEL = {
  decisive: 'Decisive',
  high: 'Key',
  medium: 'Notable',
  low: 'Minor',
};

const EVENT_TYPE_LABEL = {
  goal: 'Goal',
  save: 'Save',
  joker_shoot: 'Joker Shoot',
  joker_double_attack_result: 'Double Chance Attack',
  joker_double_placed: 'Double Chance Placed',
  penalty_reveal: 'Penalty',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function fmtAvg(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return Number(v).toFixed(1);
}

function fmtInt(v) {
  if (v == null) return '—';
  return String(v);
}

async function _fetch(path) {
  const res = await fetch(`${BACKEND}${path}`, { credentials: 'include' });
  if (res.status === 404) return { _notFound: true };
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ============================================================
export default function MatchReportScreen() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // PHASE 5.0A PASS 3 — Context-aware Back.
  //
  //   • origin === 'match_summary' → Back re-opens the same match's
  //     post-match summary via `/match-summary/:matchId`.
  //   • origin === 'profile_matches' → Back returns to Profile's
  //     Matches tab (`/profile/me?tab=matches`).
  //   • otherwise → default browser-history behavior.
  //
  // This fixes the confirmed production bug where Back from Luck Report
  // could land on the legacy Online mode-picker via history depth.
  const origin = location.state?.origin || null;
  const originMatchId = location.state?.matchId || matchId;
  const returnPath = location.state?.returnPath || null;
  const goBack = useCallback(() => {
    if (origin === 'match_summary') {
      // PHASE 5.0A PASS 3A — Prefer the dedicated read-only route
      // that restores the summary from the sessionStorage snapshot
      // written when Luck Report was opened. This is robust against
      // the live WebSocket / room having been released after match
      // finalization (root cause of the 5.0A production regression).
      //
      // Fallback order:
      //   1. `/match-summary/:matchId` if a snapshot exists
      //   2. explicit `returnPath` from origin state
      //   3. `/home`  (never `/online`  — the old lobby is the bug)
      const mid = originMatchId;
      let hasSnapshot = false;
      try {
        hasSnapshot = !!sessionStorage.getItem(`kickard.summary.${mid}`);
      } catch (_) { hasSnapshot = false; }
      if (hasSnapshot && mid) {
        navigate(`/match-summary/${encodeURIComponent(mid)}`);
        return;
      }
      if (returnPath) {
        navigate(returnPath);
        return;
      }
      navigate('/home');
      return;
    }
    if (origin === 'profile_matches') {
      navigate(returnPath || '/profile/me?tab=matches');
      return;
    }
    if (location.key === 'default') { navigate('/home'); return; }
    navigate(-1);
  }, [origin, originMatchId, returnPath, navigate, location.key]);

  const loadSeq = React.useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const live = () => seq === loadSeq.current;
    setErr(null); setReport(null); setNotFound(false);
    try {
      const guest = loadGuest();
      const practice = loadPracticeTrace(matchId);
      if (practice) {
        const res = await fetch(`${BACKEND}/api/match-reports/practice`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(practice),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (live()) setReport(d);
        return;
      }
      const gid = guest?.guestId ? `?guestId=${encodeURIComponent(guest.guestId)}` : '';
      const d = await _fetch(`/api/match-reports/${encodeURIComponent(matchId)}${gid}`);
      if (!live()) return;
      if (d?._notFound) { setNotFound(true); return; }
      setReport(d);
    } catch (e) {
      if (live()) setErr(String(e.message || e));
    }
  }, [matchId]);

  useEffect(() => { setScreen('match_report'); load(); }, [load]);

  if (notFound) {
    return (
      <ReportShell onBack={goBack}>
        <ReportTitle />
        <div data-testid="match-report-not-found" className="lrp-panel lrp-state">
          <div className="lrp-state-title">Report unavailable</div>
          <div className="lrp-state-text">Some data is unavailable for this match.</div>
        </div>
      </ReportShell>
    );
  }

  if (err) {
    return (
      <ReportShell onBack={goBack}>
        <ReportTitle />
        <div data-testid="match-report-error" className="lrp-panel lrp-state lrp-state--error">
          <div className="lrp-state-title">Couldn&apos;t load the report</div>
          <div className="lrp-state-text">{err}</div>
          <button type="button" className="lrp-retry" onClick={load} data-testid="match-report-retry">Try again</button>
        </div>
      </ReportShell>
    );
  }

  if (!report) {
    return (
      <ReportShell onBack={goBack}>
        <ReportTitle />
        <div className="lrp-panel lrp-state" data-testid="match-report-loading">
          <div className="lrp-state-text">Loading Luck Report…</div>
        </div>
      </ReportShell>
    );
  }

  const completeness = report.cardLuckCompleteness || 'missing';
  const isMissing = completeness === 'missing' || !report.cardLuckV1;
  const isPartial = completeness === 'partial';
  const isError = completeness === 'error';

  const m = report.match || {};
  const viewer = report.viewer || {};
  const cl = report.cardLuckV1;
  const p1o = cl?.players?.p1?.overallReceived;
  const p2o = cl?.players?.p2?.overallReceived;
  const p1Name = cl?.players?.p1?.displayName || m.p1DisplayName || 'Player 1';
  const p2Name = cl?.players?.p2?.displayName || m.p2DisplayName || 'Player 2';
  const hasDetail = !isMissing && !isError && !!cl;

  return (
    <ReportShell onBack={goBack}>
      {/* Header */}
      <div className="lrp-title-area" data-testid="match-report-header">
        <h1 className="lrp-title">LUCK <span>REPORT</span></h1>
        <div className="lrp-meta" data-testid="match-report-meta">
          {MODE_LABEL[m.matchType] || m.matchType || 'Match'}
          {m.endedBy && ` · ${ENDED_BY_LABEL[m.endedBy] || m.endedBy}`}
          {m.completedAt && <span className="lrp-date">{fmtDate(m.completedAt)}</span>}
        </div>
      </div>

      <div className={`lrp-grid${hasDetail ? ' lrp-grid--two' : ''}`}>
        <div className="lrp-col" data-testid="match-report-col-stats">
          {/* Scoreline */}
          <div className="lrp-panel lrp-score" data-testid="match-report-scoreline">
            <div className="lrp-score-side">
              <div className="lrp-pname" title={p1Name}>{p1Name}</div>
              <div className="lrp-goals lrp-goals--p1" data-testid="match-report-score-p1">{m.p1Score ?? '—'}</div>
            </div>
            <div className="lrp-score-side">
              <div className="lrp-pname" title={p2Name}>{p2Name}</div>
              <div className="lrp-goals lrp-goals--p2" data-testid="match-report-score-p2">{m.p2Score ?? '—'}</div>
            </div>
            {m.wentToPenalties && (
              <div className="lrp-pens" data-testid="match-report-penalties">Decided on Penalties</div>
            )}
          </div>

          {/* PHASE 4.0.4 — Ranked SR delta block (only when matchType==='ranked') */}
          {m.matchType === 'ranked' && (
            <RankedResultBlock matchId={m.matchId} />
          )}

          {(isMissing || isError) && (
            <div data-testid="match-report-unavailable" className="lrp-panel lrp-state">
              <div className="lrp-state-title">Luck data unavailable</div>
              <div className="lrp-state-text">
                Some data is unavailable for this match. Card luck tracking was added recently;
                matches played before that don&apos;t include the full ledger.
              </div>
            </div>
          )}

          {hasDetail && (
            <>
              {isPartial && (
                <div data-testid="match-report-partial" className="lrp-notice">
                  Some card data is unavailable for this match. Showing partial results below.
                </div>
              )}
              <AverageMatchCardHero
                p1Name={p1Name}
                p2Name={p2Name}
                p1Avg={p1o?.averageMatchCardValue}
                p2Avg={p2o?.averageMatchCardValue}
                comparison={cl.comparison?.averageMatchCardEdge}
              />
              <SubMetrics p1Name={p1Name} p2Name={p2Name} p1Overall={p1o} p2Overall={p2o} comparison={cl.comparison} />
              <BestWorstJokers p1Name={p1Name} p2Name={p2Name} p1Overall={p1o} p2Overall={p2o} />
              {/* Phase 3.13.3 — Joker deck accounting (only if backend supplied it) */}
              <DeckAccountingRow deckAccounting={cl.deckAccounting} />
            </>
          )}
        </div>

        {hasDetail && (
          <div className="lrp-col" data-testid="match-report-col-detail">
            <KeyCardEvents events={cl.keyCardEvents} p1Name={p1Name} p2Name={p2Name} isParticipant={viewer.isParticipant} />
            {/* Full card lists — participants only */}
            {viewer.isParticipant && (
              <FullCardLists p1Name={p1Name} p2Name={p2Name} players={cl.players} />
            )}
            {!viewer.isParticipant && (
              <div data-testid="match-report-non-participant-notice" className="lrp-notice">
                Full card lists are visible only to the two players who competed in this match.
              </div>
            )}
          </div>
        )}
      </div>
      <div className="lrp-footer">KICKARD · MATCH ANALYTICS</div>
    </ReportShell>
  );
}

// ============================================================
const LR = '/luck_report';
const LOGO = '/match_result_premium/brand/kickard_logo_existing.png';
const LRP_VARS = {
  '--lrp-stadium': 'url(/kickard/weekly-cup/weekly_stadium.webp)',
  '--lrp-metal': 'url(/match_result_premium/victory_v3/metal_smoke_surface.webp)',
};
const EVENT_ICON = {
  goal: 'football',
  save: 'save',
  joker_shoot: 'joker',
  joker_double_attack_result: 'swords',
  joker_double_placed: 'swords',
  penalty_reveal: 'football',
};

function ReportShell({ children, onBack }) {
  return (
    <div className="lrp" data-testid="match-report-root" style={LRP_VARS}>
      <div className="lrp-stage">
        <div className="lrp-topbar">
          <span className="kpn-slot kpn-slot--left"><PremiumBackButton onClick={onBack} testid="match-report-back" /></span>
          <img className="lrp-logo" src={LOGO} alt="Kickard" draggable={false} />
          <span className="kpn-slot kpn-slot--right"><PremiumHomeButton testid="match-report-home" /></span>
        </div>
        {children}
      </div>
    </div>
  );
}

function ReportTitle() {
  return (
    <div className="lrp-title-area">
      <h1 className="lrp-title">LUCK <span>REPORT</span></h1>
    </div>
  );
}

function AverageMatchCardHero({ p1Name, p2Name, p1Avg, p2Avg, comparison }) {
  const winnerSide = comparison?.side;
  const label = comparison?.label;
  const diff = comparison?.difference;
  return (
    <div data-testid="match-report-average-hero" className="lrp-panel lrp-feature">
      <div className="lrp-eyebrow">Featured metric</div>
      <h2 className="lrp-feature-title" data-testid="match-report-avg-title">Average Match Card</h2>
      <div className="lrp-avg-grid">
        <PlayerAvgTile name={p1Name} avg={p1Avg} highlight={winnerSide === 'p1'} testid="match-report-avg-p1" />
        <img className="lrp-fan" src={`${LR}/gold_cards_3x.png`} alt="" aria-hidden draggable={false} />
        <PlayerAvgTile name={p2Name} avg={p2Avg} highlight={winnerSide === 'p2'} testid="match-report-avg-p2" />
      </div>
      {label && (
        <div data-testid="match-report-avg-comparison">
          <div className="lrp-edge-label">Edge</div>
          <div className="lrp-edge">
            {label}
            {diff != null && diff > 0 && <span className="lrp-edge-diff">(Δ {fmtAvg(diff)})</span>}
          </div>
        </div>
      )}
      <div className="lrp-explain">
        <img src={`${LR}/icons/info.svg`} alt="" aria-hidden />
        <span>
          Calculated from cards dealt in both halves + all cards drawn during the match.
          Jokers are not counted in the average.
        </span>
      </div>
    </div>
  );
}

function PlayerAvgTile({ name, avg, highlight, testid }) {
  return (
    <div data-testid={testid} data-highlight={highlight ? 'true' : 'false'} className={`lrp-avg${highlight ? ' lrp-avg--edge' : ''}`}>
      <div className="lrp-avg-name" title={name}>{name}</div>
      <div className="lrp-avg-val" data-testid={`${testid}-value`}>{fmtAvg(avg)}</div>
      <div className="lrp-avg-sub">Avg card</div>
    </div>
  );
}

// ------------------------------------------------------------
function SubMetrics({ p1Name, p2Name, p1Overall, p2Overall, comparison }) {
  const rows = [
    { label: '1st half — dealt avg', p1: p1Overall?.firstHalfDealtAverageValue, p2: p2Overall?.firstHalfDealtAverageValue, edge: comparison?.firstHalfDealAdvantage },
    { label: '2nd half — dealt avg', p1: p1Overall?.secondHalfDealtAverageValue, p2: p2Overall?.secondHalfDealtAverageValue, edge: comparison?.secondHalfDealAdvantage },
    { label: 'Drawn cards — overall avg', p1: p1Overall?.allDrawnAverageValue, p2: p2Overall?.allDrawnAverageValue, edge: comparison?.drawnCardAdvantage },
  ];
  return (
    <div className="lrp-section" data-testid="match-report-submetrics">
      <SectionTitle>Per-half breakdown</SectionTitle>
      <div className="lrp-table">
        <HeaderRow p1Name={p1Name} p2Name={p2Name} />
        {rows.map((r, i) => (
          <MetricRow key={i} label={r.label} p1={fmtAvg(r.p1)} p2={fmtAvg(r.p2)} winner={r.edge?.side} testid={`match-report-submetric-${i}`} />
        ))}
      </div>
    </div>
  );
}

function BestWorstJokers({ p1Name, p2Name, p1Overall, p2Overall }) {
  const rows = [
    { label: 'Best received card', p1: cardLabel(p1Overall?.bestReceivedCard), p2: cardLabel(p2Overall?.bestReceivedCard) },
    { label: 'Worst received card', p1: cardLabel(p1Overall?.worstReceivedCard), p2: cardLabel(p2Overall?.worstReceivedCard) },
    { label: 'High cards received (11+)', p1: fmtInt(p1Overall?.highCardsReceived), p2: fmtInt(p2Overall?.highCardsReceived) },
    { label: 'Low cards received (≤5)', p1: fmtInt(p1Overall?.lowCardsReceived), p2: fmtInt(p2Overall?.lowCardsReceived) },
    { label: 'Jokers received', p1: fmtInt(p1Overall?.jokersReceived), p2: fmtInt(p2Overall?.jokersReceived) },
  ];
  return (
    <div className="lrp-section" data-testid="match-report-bestworst">
      <SectionTitle>Card quality</SectionTitle>
      <div className="lrp-table lrp-table--quality">
        <HeaderRow p1Name={p1Name} p2Name={p2Name} />
        {rows.map((r, i) => (
          <MetricRow key={i} label={r.label} p1={r.p1} p2={r.p2} testid={`match-report-quality-${i}`} />
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Phase 3.13.3 — Joker deck accounting line. Copy lives in
// `./deckAccountingCopy`; renders nothing without backend deckAccounting.
// ------------------------------------------------------------
function DeckAccountingRow({ deckAccounting }) {
  const copy = computeDeckAccountingCopy(deckAccounting);
  if (!copy.shouldRender) return null;
  const { mismatch, headline, subtext } = copy;
  return (
    <div className="lrp-section" data-testid="match-report-deck-accounting">
      <div
        className={`lrp-panel lrp-jokers${mismatch ? ' lrp-jokers--mismatch' : ''}`}
        data-testid={mismatch ? 'match-report-deck-accounting-mismatch' : 'match-report-deck-accounting-ok'}
      >
        <img src={`${LR}/icons/crown.svg`} alt="" aria-hidden />
        <div className="lrp-jokers-copy">
          <div className="lrp-jokers-head" data-testid="match-report-deck-accounting-headline">{headline}</div>
          <div className="lrp-jokers-sub" data-testid="match-report-deck-accounting-subtext">{subtext}</div>
        </div>
      </div>
    </div>
  );
}

function cardLabel(c) {
  if (!c) return '—';
  return c.label || `${c.rank || '?'} of ${c.suit || '?'}`;
}

function SectionTitle({ children }) {
  return <h3 className="lrp-section-title">{children}</h3>;
}

function HeaderRow({ p1Name, p2Name }) {
  return (
    <div className="lrp-row lrp-row--head">
      <div>Metric</div>
      <div className="lrp-v" title={p1Name}>{p1Name}</div>
      <div className="lrp-v" title={p2Name}>{p2Name}</div>
    </div>
  );
}

function MetricRow({ label, p1, p2, winner, testid }) {
  return (
    <div className="lrp-row" data-testid={testid}>
      <div className="lrp-metric">{label}</div>
      <div className={`lrp-v${winner === 'p1' ? ' lrp-hot' : ''}`}>{p1}</div>
      <div className={`lrp-v${winner === 'p2' ? ' lrp-hot' : ''}`}>{p2}</div>
    </div>
  );
}

// ------------------------------------------------------------
function KeyCardEvents({ events, p1Name, p2Name, isParticipant }) {
  const list = Array.isArray(events) ? events : [];
  if (!list.length) {
    return (
      <div className="lrp-section lrp-section--first" data-testid="match-report-events-empty">
        <SectionTitle>Key card events</SectionTitle>
        <div className="lrp-notice">No key card events recorded for this match.</div>
      </div>
    );
  }
  const nameFor = (side) => (side === 'p1' ? p1Name : side === 'p2' ? p2Name : '—');
  return (
    <div className="lrp-section lrp-section--first" data-testid="match-report-events">
      <SectionTitle>Key card events</SectionTitle>
      <div className="lrp-events">
        {list.map((ev, i) => (
          <div
            key={i}
            className={`lrp-event lrp-event--${ev.importance || 'none'}`}
            data-testid={`match-report-event-${i}`}
            data-event-type={ev.eventType || ''}
          >
            <img className="lrp-event-icon" src={`${LR}/icons/${EVENT_ICON[ev.eventType] || 'bolt'}.svg`} alt="" aria-hidden />
            <div className="lrp-event-body">
              <div className="lrp-event-top">
                <ImportancePill importance={ev.importance} />
                <span className="lrp-event-type">{EVENT_TYPE_LABEL[ev.eventType] || ev.eventType}</span>
                {ev.actorSide && <span className="lrp-event-meta">· {nameFor(ev.actorSide)}</span>}
                {ev.half && <span className="lrp-event-meta lrp-event-half">{ev.half === 'first' ? '1st half' : '2nd half'}</span>}
              </div>
              {isParticipant && Array.isArray(ev.cards) && ev.cards.length > 0 && (
                <div className="lrp-chips">
                  {ev.cards.map((c, j) => <CardChip key={j} card={c} />)}
                </div>
              )}
              {ev.outcome && <div className="lrp-event-outcome">Outcome · {ev.outcome}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportancePill({ importance }) {
  return (
    <span className={`lrp-badge lrp-badge--${importance || 'none'}`}>
      {IMPORTANCE_LABEL[importance] || importance || '—'}
    </span>
  );
}

function CardChip({ card }) {
  const kind = card?.isJoker ? 'joker' : (card?.bucket === 'high' ? 'high' : card?.bucket === 'low' ? 'low' : 'mid');
  return <span className={`lrp-chip lrp-chip--${kind}`}>{cardLabel(card)}</span>;
}

// ------------------------------------------------------------
function FullCardLists({ p1Name, p2Name, players }) {
  return (
    <div className="lrp-section" data-testid="match-report-full-lists">
      <SectionTitle>Full card ledger</SectionTitle>
      <p className="lrp-note">Visible only to match participants. Every card you received during this match.</p>
      <PlayerFullLists name={p1Name} p={players?.p1} testid="match-report-full-p1" />
      <PlayerFullLists name={p2Name} p={players?.p2} testid="match-report-full-p2" />
    </div>
  );
}

function PlayerFullLists({ name, p, testid }) {
  if (!p) return null;
  return (
    <div className="lrp-ledger" data-testid={testid}>
      <div className="lrp-ledger-head">
        <img src={`${LR}/icons/cards.svg`} alt="" aria-hidden />
        <span>{name}</span>
      </div>
      <HalfBlock title="1st half" data={p.firstHalf} />
      <HalfBlock title="2nd half" data={p.secondHalf} />
    </div>
  );
}

function HalfBlock({ title, data }) {
  if (!data) return null;
  const dealt = Array.isArray(data.dealtCards) ? data.dealtCards : [];
  const drawn = Array.isArray(data.drawnCards) ? data.drawnCards : [];
  return (
    <div className="lrp-ledger-half">
      <div className="lrp-halfname">{title}</div>
      <MiniList label="Dealt in formation" avg={data.dealtAverageValue} cards={dealt} />
      <MiniList label="Drawn during play" avg={data.drawnAverageValue} cards={drawn} />
    </div>
  );
}

function MiniList({ label, avg, cards }) {
  return (
    <div className="lrp-group">
      <div className="lrp-group-head">
        <span>{label}</span>
        <span className="lrp-group-avg">AVG <b>{fmtAvg(avg)}</b></span>
      </div>
      {cards.length === 0 ? (
        <div className="lrp-empty">—</div>
      ) : (
        <div className="lrp-chips">
          {cards.map((c, i) => <CardChip key={i} card={c} />)}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// PHASE 4.0.4 — Ranked SR delta block. Renders inside MatchReportScreen
// only when `m.matchType === 'ranked'`. Fetches the participant-safe
// ranked result and shows SR delta, tier change, and placement progress.
// -------------------------------------------------------------------
function RankedResultBlock({ matchId }) {
  const [result, setResult] = React.useState(null);
  const [tries, setTries] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    const guest = loadGuest();
    async function fetchOnce() {
      const r = await getRankedResult(matchId, guest?.guestId);
      if (cancelled) return;
      setResult(r);
      // Retry a few times if result is still pending.
      if (r.ok && r.status === 'pending' && tries < 4) {
        setTimeout(() => setTries(t => t + 1), 2000);
      }
    }
    fetchOnce();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, tries]);

  if (!result) {
    return (
      <div className="lrp-panel lrp-ranked"
           data-testid="ranked-result-loading">
        <div className="text-white/60 text-sm">Ranked result loading…</div>
      </div>
    );
  }
  if (!result.ok) {
    // 403 non-participant or network error → hide the block silently.
    return null;
  }
  if (result.status === 'pending') {
    return (
      <div className="lrp-panel lrp-ranked"
           data-testid="ranked-result-pending">
        <div className="text-white/70 text-sm font-medium">Ranked Result</div>
        <div className="text-white/50 text-xs mt-1">Result processing…</div>
      </div>
    );
  }
  if (result.status === 'ignored' || result.status === 'not_ranked') {
    return (
      <div className="lrp-panel lrp-ranked"
           data-testid="ranked-result-unavailable">
        <div className="text-white/60 text-sm">Ranked result unavailable.</div>
      </div>
    );
  }
  // Processed.
  const p = result.player || {};
  const delta = Number(p.srDelta || 0);
  const win = delta > 0;
  const tierChange = formatTierChange(p.beforeTier, p.afterTier);
  const placementText = p.placementComplete
    ? 'Placement complete'
    : `Placement match ${p.placementMatchesPlayed} / ${p.placementMatchesRequired}`;
  return (
    <div className="lrp-panel lrp-ranked"
         data-testid="ranked-result-block">
      <div className="text-[hsl(var(--k-gold-bright))]/90 text-xs uppercase tracking-[0.28em] mb-2">
        Ranked Result
      </div>
      <div className={`text-3xl font-semibold ${win ? 'text-green-300' : 'text-red-300'}`}
           data-testid="ranked-result-sr-delta">
        {formatSRDelta(delta)}
      </div>
      <div className="text-white/55 text-xs mt-1" data-testid="ranked-result-placement">
        {placementText}
      </div>
      {tierChange && (
        <div className="text-amber-300 text-sm mt-2" data-testid="ranked-result-tier-change">
          {tierChange}
        </div>
      )}
      <div className="text-white/40 text-[11px] mt-3">
        Preseason ratings may reset before Season 1.
      </div>
    </div>
  );
}

